import React, { useMemo } from 'react'
import type { DocumentInsight, DocumentInsightKind } from '../../../../../services/projectAutofillDraftService.ts'
import styles from './DocumentInsightPanel.module.scss'

const KNOWN_ACTION_IDS = new Set([
  'upload_technical_doc',
  'continue_anyway',
  'contact_support'
])

function documentInsightKindLabelEs(kind: DocumentInsightKind): string {
  switch (kind) {
    case 'project_technical':
      return 'documentación técnica de obra'
    case 'materials_list':
      return 'lista de materiales o presupuesto'
    case 'invoice_or_admin':
      return 'factura u otro documento administrativo'
    case 'unreadable_or_empty':
      return 'documento ilegible o sin contenido útil'
    case 'other':
      return 'otro tipo de documento'
    case 'unclear':
      return 'tipo de documento poco claro'
    default:
      return 'documento'
  }
}

const SUPPORT_MAIL = 'contacto@edify.la'

export type DocumentInsightPanelHandlers = {
  onUploadTechnicalDoc?: () => void
  onContinueAnyway?: () => void
}

type DocumentInsightPanelProps = {
  insight: DocumentInsight
  handlers?: DocumentInsightPanelHandlers
}

const DocumentInsightPanel: React.FC<DocumentInsightPanelProps> = ({ insight, handlers }) => {
  const title = useMemo(() => {
    const t = insight.titleForUser
    if (insight.confidence < 0.75) {
      const lower = t.charAt(0).toLowerCase() + t.slice(1)
      return `Podría ser que ${lower}`
    }
    return t
  }, [insight.confidence, insight.titleForUser])

  const wiredActions = useMemo(() => {
    const filtered = insight.suggestedActions.filter((a) => KNOWN_ACTION_IDS.has(a.id))
    const invitesNewUpload = filtered.some((a) => a.id === 'upload_technical_doc')
    return filtered.filter((a) => {
      if (a.id === 'continue_anyway' && !invitesNewUpload) return false
      return true
    })
  }, [insight.suggestedActions])

  const agentDetectionLine = useMemo(() => {
    const kindLabel = documentInsightKindLabelEs(insight.kind)
    if (insight.confidence < 0.75) {
      return `El asistente interpretó el archivo como ${kindLabel} (confianza moderada).`
    }
    return `El asistente interpretó el archivo como ${kindLabel}.`
  }, [insight.kind, insight.confidence])

  const onAction = (id: string) => {
    if (id === 'upload_technical_doc') {
      handlers?.onUploadTechnicalDoc?.()
      return
    }
    if (id === 'continue_anyway') {
      handlers?.onContinueAnyway?.()
      return
    }
    if (id === 'contact_support') {
      window.open(`mailto:${SUPPORT_MAIL}?subject=Consulta%20sobre%20documentaci%C3%B3n%20Edify`, '_blank')
    }
  }

  const entendidoInHeader = wiredActions.length === 0 && !!handlers?.onContinueAnyway

  return (
    <div className={styles.wrap} role="region" aria-label="Clasificación del documento">
      <div className={styles.headRow}>
        <p className={styles.title}>{title}</p>
        {entendidoInHeader ? (
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.headEntendidoBtn}`}
            onClick={() => handlers.onContinueAnyway?.()}
          >
            Entendido
          </button>
        ) : null}
      </div>
      <p className={styles.agentLine}>{agentDetectionLine}</p>
      {insight.bodyForUser ? <p className={styles.body}>{insight.bodyForUser}</p> : null}
      {wiredActions.length > 0 ? (
        <div className={styles.actions}>
          {wiredActions.map((a) => (
            <button
              key={`${a.id}-${a.label}`}
              type="button"
              className={styles.actionBtn}
              onClick={() => onAction(a.id)}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default DocumentInsightPanel
